import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { newAgreementToken } from '@/lib/crew'
import { formatAgo } from '@/lib/dates'
import { useUpdateJob, type JobDetail } from '@/features/pipeline/queries'

export function portalLink(token: string): string {
  return `${window.location.origin}/p/${token}`
}

/**
 * Top right of a project, like Studio Ninja (28 Oct 2026): "View client
 * portal" opens exactly what the couple sees, in a new tab, without counting
 * as their visit. The link is made the first time it is needed and is only
 * ever shared by Jena herself ("Copy client link").
 */
export function PortalActions({ job }: { job: JobDetail }) {
  const update = useUpdateJob(job.id)

  async function ensureToken(): Promise<string> {
    if (job.portal_token) return job.portal_token
    const token = newAgreementToken()
    const saved = await update.mutateAsync({ portal_token: token })
    return saved.portal_token ?? token
  }

  return (
    <>
      <Button
        variant="primary"
        loading={update.isPending}
        loadingLabel="Opening"
        onClick={() => {
          // Open the tab first: a tab opened after a wait is blocked.
          const tab = window.open('', '_blank')
          ensureToken().then(
            (token) => {
              const url = `${portalLink(token)}?preview=1`
              if (tab) tab.location.href = url
              else window.location.href = url
            },
            () => tab?.close(),
          )
        }}
      >
        View client portal
      </Button>
      <Button
        variant="secondary"
        onClick={() => {
          void ensureToken()
            .then((token) => navigator.clipboard.writeText(portalLink(token)))
            .then(() =>
              toast.success('Client link copied', {
                description: job.portal_opened_at
                  ? `They last opened it ${formatAgo(job.portal_opened_at)}.`
                  : 'Paste it into your email or WhatsApp to the couple when you are ready.',
              }),
            )
        }}
      >
        Copy client link
      </Button>
    </>
  )
}
